import {
  ArchonIcon,
  BarChartIcon,
  CalendarIcon,
  CardStackIcon,
  DashboardIcon,
  GearIcon,
  IconSize,
  PersonIcon,
  PlusIcon,
  SkeletonBlock,
  TargetIcon,
} from '@protoss/ui';
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useModuleMenu } from '../features/module-menu/hooks/useModuleMenu';
import { NavItem } from './shell';
import './shell/app-nav.css';

const ICON_MAP: Record<string, React.ReactNode> = {
  dashboard: <DashboardIcon width={IconSize.lg} height={IconSize.lg} aria-hidden="true" />,
  microphone: <ArchonIcon size={IconSize.lg} isRunning />,
  calendar: <CalendarIcon width={IconSize.lg} height={IconSize.lg} aria-hidden="true" />,
  person: <PersonIcon width={IconSize.lg} height={IconSize.lg} aria-hidden="true" />,
  plus: <PlusIcon width={IconSize.lg} height={IconSize.lg} aria-hidden="true" />,
  target: <TargetIcon width={IconSize.lg} height={IconSize.lg} aria-hidden="true" />,
  'card-stack': <CardStackIcon width={IconSize.lg} height={IconSize.lg} aria-hidden="true" />,
  'bar-chart': <BarChartIcon width={IconSize.lg} height={IconSize.lg} aria-hidden="true" />,
};

function buildModuleItemRoute(defaultRoute: string | null | undefined, routeSegment: string) {
  const base = defaultRoute?.trim() || '/';
  if (base === '/' || base === '') {
    return `/${routeSegment}`;
  }
  return `${base.replace(/\/+$/, '')}/${routeSegment}`;
}

/**
 * PermanentNavItem - a nav item always shown regardless of active module.
 * Use for page-level items (e.g. Live Meeting) that are independent of module context.
 */
export interface PermanentNavItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

export interface AppNavProps {
  /** Which item is currently selected: the module primary item, a module-menu route segment, administration, or neither */
  selected: string | null;
  /** Whether to hide the default module primary item */
  hidePrimary?: boolean;
  /** Whether to show the Administration nav item */
  isWorkspaceAdmin: boolean;
  /** Handler for the Administration nav item */
  onAdministrationClick: () => void;
  /**
   * Optional extra handler called when the primary (module root) nav item is clicked,
   * in addition to the default navigate. Use to open the context pane.
   */
  onPrimaryClick?: () => void;
  /**
   * Optional interception for module route-item clicks.
   * Return true when the caller handled the click and AppNav should skip its default navigation.
   */
  onRouteItemClick?: (routeSegment: string, targetRoute: string) => boolean;
  /** Optional nav items always shown regardless of active module (e.g. Live Meeting) */
  permanentItems?: PermanentNavItem[];
}

/**
 * AppNav - shared primary left navigation content
 *
 * Reads the active module (navLabel, navIconKey, defaultRoute) from WorkspaceContext
 * and renders a DB-driven primary nav item + optional Administration item.
 *
 * Architecture:
 * - Belongs in apps/web (uses NavItem from shell, follows app-layer rules)
 * - Reads activeModule internally - callers only need to pass selection state
 * - CSS is co-located in ./shell/app-nav.css (imported here)
 */
export function AppNav({
  selected,
  hidePrimary = false,
  isWorkspaceAdmin,
  onAdministrationClick,
  onPrimaryClick,
  onRouteItemClick,
  permanentItems,
}: AppNavProps) {
  const { activeModule, isLoadingModules } = useWorkspace();
  const { items: moduleMenuItems, isLoading: isLoadingMenu } = useModuleMenu();
  const location = useLocation();
  // When already on an admin route, keep the link pointing to the current path so
  // clicking the gear icon is a no-op rather than resetting the admin section.
  const adminHref = location.pathname.startsWith('/administration')
    ? location.pathname
    : '/administration';

  const isLoading = isLoadingModules || isLoadingMenu;

  const primaryLabel = activeModule?.navLabel ?? 'Home';
  const primaryIconKey = activeModule?.navIconKey ?? 'dashboard';
  const primaryIcon = ICON_MAP[primaryIconKey] ?? ICON_MAP.dashboard;
  const enabledModuleItems = moduleMenuItems.filter((item) => item.isEnabled);
  const actionItems = enabledModuleItems.filter((item) => item.menuGroupKey === 'module-action');
  const navItems = enabledModuleItems.filter((item) => item.menuGroupKey !== 'module-action');

  return (
    <nav className="home-sidebar-nav" aria-label="Primary navigation">
      <div className="nav-section">
        {isLoading ? (
          <>
            <SkeletonBlock className="nav-item-skeleton nav-item-skeleton--primary" />
            <SkeletonBlock className="nav-item-skeleton" />
            <SkeletonBlock className="nav-item-skeleton" />
          </>
        ) : (
          <>
            {!hidePrimary && (
              <NavItem
                id="primary"
                icon={primaryIcon}
                label={primaryLabel}
                selected={selected === 'primary'}
                href={activeModule?.defaultRoute ?? '/'}
                onClick={() => {
                  onPrimaryClick?.();
                }}
              />
            )}
            {actionItems.map((item) =>
              (() => {
                const targetRoute = buildModuleItemRoute(
                  activeModule?.defaultRoute,
                  item.routeSegment
                );

                return (
                  <NavItem
                    key={item.key}
                    id={item.routeSegment}
                    icon={ICON_MAP[item.iconKey] ?? ICON_MAP.plus}
                    label={item.label}
                    selected={selected === item.routeSegment}
                    variant="action"
                    href={targetRoute}
                    onClick={() => {
                      onRouteItemClick?.(item.routeSegment, targetRoute);
                    }}
                  />
                );
              })()
            )}
            {navItems.map((item) =>
              (() => {
                const targetRoute = buildModuleItemRoute(
                  activeModule?.defaultRoute,
                  item.routeSegment
                );

                return (
                  <NavItem
                    key={item.key}
                    id={item.routeSegment}
                    icon={ICON_MAP[item.iconKey] ?? ICON_MAP.dashboard}
                    label={item.label}
                    selected={selected === item.routeSegment}
                    href={targetRoute}
                    onClick={() => {
                      onRouteItemClick?.(item.routeSegment, targetRoute);
                    }}
                  />
                );
              })()
            )}
            {permanentItems?.map((item) => (
              <NavItem
                key={item.id}
                id={item.id}
                icon={item.icon}
                label={item.label}
                selected={selected === item.id}
                onClick={item.onClick}
              />
            ))}
          </>
        )}
      </div>

      {isWorkspaceAdmin && (
        <div className="nav-section nav-section-bottom">
          <NavItem
            id="administration"
            icon={<GearIcon width={IconSize.lg} height={IconSize.lg} aria-hidden="true" />}
            label="Administration"
            selected={selected === 'administration'}
            href={adminHref}
            onClick={onAdministrationClick}
          />
        </div>
      )}
    </nav>
  );
}
